import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";

const MONEY_ICONS = ["🏦", "💰", "💳", "📈", "💵", "🪙", "💸", "💼"];
const GRID_SIZE = 5;
const MAX_MOVES = 12;
const POINTS_PER_MATCH = 10;

function getRandomEmoji(exclude = []) {
  const choices = MONEY_ICONS.filter((e) => !exclude.includes(e));
  return choices[Math.floor(Math.random() * choices.length)];
}

function generateGrid() {
  const grid = [];
  for (let row = 0; row < GRID_SIZE; row++) {
    grid[row] = [];
    for (let col = 0; col < GRID_SIZE; col++) {
      let exclude = [];
      if (col >= 2 && grid[row][col - 1] === grid[row][col - 2]) {
        exclude.push(grid[row][col - 1]);
      }
      if (row >= 2 && grid[row - 1][col] === grid[row - 2][col]) {
        exclude.push(grid[row - 1][col]);
      }
      grid[row][col] = getRandomEmoji(exclude);
    }
  }
  return grid;
}

function cloneGrid(grid) {
  return grid.map((row) => [...row]);
}

function isValidSwap(grid, r1, c1, r2, c2) {
  const newGrid = cloneGrid(grid);
  [newGrid[r1][c1], newGrid[r2][c2]] = [newGrid[r2][c2], newGrid[r1][c1]];
  return findMatches(newGrid).length > 0;
}

function findMatches(grid) {
  const found = [];
  // Horizontal
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE - 2; col++) {
      if (
        grid[row][col] === grid[row][col + 1] &&
        grid[row][col] === grid[row][col + 2]
      ) {
        found.push({ row, col }, { row, col: col + 1 }, { row, col: col + 2 });
      }
    }
  }
  // Vertical
  for (let col = 0; col < GRID_SIZE; col++) {
    for (let row = 0; row < GRID_SIZE - 2; row++) {
      if (
        grid[row][col] === grid[row + 1][col] &&
        grid[row][col] === grid[row + 2][col]
      ) {
        found.push({ row, col }, { row: row + 1, col }, { row: row + 2, col });
      }
    }
  }
  return found.filter(
    (pos, idx, arr) =>
      arr.findIndex((p) => p.row === pos.row && p.col === pos.col) === idx
  );
}

const MoneyMatch3Game = () => {
  const [grid, setGrid] = useState(generateGrid());
  const [selected, setSelected] = useState(null);
  const [matches, setMatches] = useState([]);
  const [movesLeft, setMovesLeft] = useState(MAX_MOVES);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [animating, setAnimating] = useState(false);

  function handleTileClick(row, col) {
    if (gameOver || movesLeft <= 0 || animating) return;
    if (selected) {
      const dr = Math.abs(selected.row - row);
      const dc = Math.abs(selected.col - col);
      if (dr + dc === 1) {
        const newGrid = cloneGrid(grid);
        [newGrid[selected.row][selected.col], newGrid[row][col]] = [
          newGrid[row][col],
          newGrid[selected.row][selected.col],
        ];
        const found = findMatches(newGrid);
        setMovesLeft((prev) => prev - 1);
        if (found.length > 0) {
          setGrid(newGrid);
          setMatches(found);
          setScore(
            (prev) => prev + POINTS_PER_MATCH * Math.floor(found.length / 3)
          );
          setAnimating(true);
          setTimeout(() => {
            const afterMatch = cloneGrid(newGrid);
            found.forEach(({ row, col }) => {
              afterMatch[row][col] = getRandomEmoji();
            });
            setGrid(afterMatch);
            setMatches([]);
            setAnimating(false);
          }, 700);
          setTimeout(() => {
            if (movesLeft - 1 <= 0) setGameOver(true);
          }, 750);
        } else {
          setGrid(newGrid);
          setAnimating(false);
          setTimeout(() => {
            if (movesLeft - 1 <= 0) setGameOver(true);
          }, 750);
        }
        setSelected(null);
      } else {
        setSelected({ row, col });
      }
    } else {
      setSelected({ row, col });
    }
  }

  function handleReset() {
    setGrid(generateGrid());
    setSelected(null);
    setMatches([]);
    setMovesLeft(MAX_MOVES);
    setScore(0);
    setGameOver(false);
    setAnimating(false);
  }

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #F0F7FF 0%, #E6F3FF 100%)",
        border: "4px solid #1E40AF",
        borderRadius: "24px",
        p: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 320,
        maxWidth: 400,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "url('/credit-union-logo-watermark.png')",
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          opacity: 0.05,
          pointerEvents: "none",
        },
      }}
    >
      <Typography
        variant="h5"
        sx={{
          mb: 2,
          color: "#1E40AF",
          fontWeight: 700,
          textAlign: "center",
          fontSize: "1.5rem",
        }}
      >
        Stay Connected to Smarter Money Habits — One Match at a Time
      </Typography>
      <Typography
        sx={{
          mb: 2,
          color: "#2563EB",
          fontWeight: 500,
          textAlign: "center",
          fontSize: "0.9rem",
          lineHeight: 1.4,
        }}
      >
        A simple, interactive way to keep financial wellness top of mind — while
        offering a fun daily touchpoint.
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Typography sx={{ color: "#2563EB", fontWeight: 600 }}>
          Moves Remaining: {movesLeft}
        </Typography>
        <Typography sx={{ color: "#1E40AF", fontWeight: 600 }}>
          Savings Points: {score}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${GRID_SIZE}, 40px)`,
          gridTemplateRows: `repeat(${GRID_SIZE}, 40px)`,
          gap: 1,
          mb: 2,
          pointerEvents: gameOver ? "none" : "auto",
          opacity: gameOver ? 0.5 : 1,
        }}
      >
        {grid.map((rowArr, row) =>
          rowArr.map((emoji, col) => {
            const isSelected =
              selected && selected.row === row && selected.col === col;
            const isMatched = matches.some(
              (m) => m.row === row && m.col === col
            );
            return (
              <Button
                key={`${row}-${col}`}
                onClick={() => handleTileClick(row, col)}
                sx={{
                  minWidth: 0,
                  width: 40,
                  height: 40,
                  fontSize: "1.6rem",
                  borderRadius: "12px",
                  border: isSelected
                    ? "2px solid #1E40AF"
                    : isMatched
                    ? "2px solid #059669"
                    : "2px solid #F0F7FF",
                  background:
                    isMatched && animating
                      ? "#D1FAE5"
                      : isSelected
                      ? "#DBEAFE"
                      : "white",
                  boxShadow:
                    isMatched && animating
                      ? "0 0 12px 2px #059669AA"
                      : isSelected
                      ? "0 0 6px 1px #1E40AF55"
                      : "0 1px 2px #0001",
                  transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
                  m: 0,
                  p: 0,
                  transform:
                    isMatched && animating ? "scale(1.15)" : "scale(1)",
                  animation: isMatched && animating ? "shake 0.7s" : undefined,
                }}
              >
                {emoji}
              </Button>
            );
          })
        )}
      </Box>
      <Typography
        sx={{
          color: "#64748B",
          fontSize: "0.8rem",
          textAlign: "center",
          mb: 2,
          maxWidth: "320px",
          lineHeight: 1.4,
        }}
      >
        Games like these help build daily engagement, reinforce smart money
        habits, and keep members coming back.
      </Typography>
      <style>{`
        @keyframes shake {
          0% { transform: scale(1.15) translateX(0); }
          20% { transform: scale(1.15) translateX(-4px); }
          40% { transform: scale(1.15) translateX(4px); }
          60% { transform: scale(1.15) translateX(-2px); }
          80% { transform: scale(1.15) translateX(2px); }
          100% { transform: scale(1.15) translateX(0); }
        }
      `}</style>
      {gameOver ? (
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography
            sx={{
              color: "#DC2626",
              fontWeight: 700,
              fontSize: "1.2rem",
              mb: 1,
            }}
          >
            Game Over!
          </Typography>
          <Typography
            sx={{
              color: "#1E40AF",
              fontWeight: 600,
              fontSize: "1.1rem",
              mb: 2,
            }}
          >
            Your Savings Points: {score}
          </Typography>
          <Button
            variant="contained"
            onClick={handleReset}
            sx={{
              background: "#1E40AF",
              color: "#fff",
              fontWeight: 700,
              borderRadius: "100px",
              px: 4,
              mt: 1,
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              fontSize: "1.1rem",
              "&:hover": {
                background: "#2563EB",
                color: "#fff",
              },
            }}
          >
            Play Again & Stay Connected
          </Button>
        </Box>
      ) : (
        <Button
          variant="contained"
          onClick={handleReset}
          sx={{
            background: "#1E40AF",
            color: "#fff",
            fontWeight: 700,
            borderRadius: "100px",
            px: 4,
            mt: 1,
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            fontSize: "1.1rem",
            "&:hover": {
              background: "#2563EB",
              color: "#fff",
            },
          }}
        >
          Start Over
        </Button>
      )}
    </Box>
  );
};

export default MoneyMatch3Game;
